/**
 * Customer Portal (UI) - ApexCharts-based dashboard visualizations.
 *
 * Encapsulates chart option defaults so route components can pass typed series
 * data without repeating chart configuration.
 */
import type { ApexOptions } from 'apexcharts';
import type { ReactNode } from 'react';
import ReactApexChart from 'react-apexcharts';

type VolumePoint = {
  hourUtc: string;
  smsOutbound: number;
  smsInbound: number;
  mmsOutbound: number;
  mmsInbound: number;
};

type DeliveryPoint = {
  hourUtc: string;
  delivered: number;
  failed: number;
  received: number;
};

type ChartCardProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
};

function ChartCard({ title, subtitle, children }: ChartCardProps) {
  return (
    <div className="card h-100">
      <div className="card-header">
        <div>
          <h3 className="card-title mb-0">{title}</h3>
          {subtitle ? <div className="text-secondary small mt-1">{subtitle}</div> : null}
        </div>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function toHourLabel(hourUtc: string): string {
  const date = new Date(hourUtc);
  if (Number.isNaN(date.getTime())) return hourUtc;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function StackedVolumeChartCard({ points }: { points: VolumePoint[] }) {
  const categories = points.map((point) => toHourLabel(point.hourUtc));
  const options: ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      animations: { enabled: false }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 2,
        columnWidth: '72%'
      }
    },
    dataLabels: { enabled: false },
    legend: { position: 'top', fontSize: '12px' },
    xaxis: {
      categories,
      labels: { rotate: -30 }
    },
    yaxis: {
      min: 0,
      forceNiceScale: true
    },
    tooltip: {
      shared: true,
      intersect: false
    },
    colors: ['#206bc4', '#79a6dc', '#2fb344', '#8bd6a2']
  };

  const series = [
    { name: 'SMS Outbound', data: points.map((point) => point.smsOutbound) },
    { name: 'SMS Inbound', data: points.map((point) => point.smsInbound) },
    { name: 'MMS Outbound', data: points.map((point) => point.mmsOutbound) },
    { name: 'MMS Inbound', data: points.map((point) => point.mmsInbound) }
  ];

  return (
    <ChartCard title="SMS/MMS Volume by Direction" subtitle="Stacked daily message volume">
      <ReactApexChart type="bar" height={320} options={options} series={series} />
    </ChartCard>
  );
}

export function SmsMmsVolumeLineChartCard({ points }: { points: Array<{ hourUtc: string; smsTotal: number; mmsTotal: number }> }) {
  const categories = points.map((point) => toHourLabel(point.hourUtc));
  const options: ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      animations: { enabled: false }
    },
    stroke: {
      width: [2, 2],
      curve: 'smooth'
    },
    dataLabels: { enabled: false },
    markers: { size: 0 },
    legend: { position: 'top', fontSize: '12px' },
    xaxis: {
      categories,
      labels: { rotate: -30 }
    },
    yaxis: {
      min: 0,
      forceNiceScale: true
    },
    tooltip: {
      shared: true,
      intersect: false
    },
    colors: ['#206bc4', '#2fb344']
  };

  const series = [
    { name: 'SMS Total', data: points.map((point) => point.smsTotal) },
    { name: 'MMS Total', data: points.map((point) => point.mmsTotal) }
  ];

  return (
    <ChartCard title="Message Volume (Rolling Range)" subtitle="Daily SMS and MMS totals (inbound + outbound)">
      <ReactApexChart type="line" height={320} options={options} series={series} />
    </ChartCard>
  );
}

export function DeliveryTrendChartCard({ points }: { points: DeliveryPoint[] }) {
  const categories = points.map((point) => toHourLabel(point.hourUtc));
  const options: ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      animations: { enabled: false }
    },
    stroke: {
      width: [2, 2, 2],
      curve: 'smooth'
    },
    dataLabels: { enabled: false },
    markers: { size: 0 },
    legend: { position: 'top', fontSize: '12px' },
    xaxis: {
      categories,
      labels: { rotate: -30 }
    },
    yaxis: {
      min: 0,
      forceNiceScale: true
    },
    tooltip: {
      shared: true,
      intersect: false
    },
    colors: ['#2fb344', '#d63939', '#f59f00']
  };

  const series = [
    { name: 'Delivered', data: points.map((point) => point.delivered) },
    { name: 'Failed', data: points.map((point) => point.failed) },
    { name: 'Received', data: points.map((point) => point.received) }
  ];

  return (
    <ChartCard title="Delivery Outcome Trend" subtitle="Delivered, failed, and received counts by day">
      <ReactApexChart type="line" height={320} options={options} series={series} />
    </ChartCard>
  );
}

